import { ALERT_TYPE_MAPPING } from "../General/alertTypes";
import { Comparator } from "react-bootstrap-table2-filter";

export const FILTER_STORAGE_KEYS = {
  LINE: "beveragesHomePageLineFilterValue",
  START_TIME: "beveragesHomePageStartTimeFilterValue",
  SHIFT: "beveragesHomePageShiftFilterValue",
  OBJECT_GROUP: "beveragesHomePageObjectGroupFilterValue",
  OBJECT_NAME: "beveragesHomePageObjectNameFilterValue",
  RECIPE: "beveragesHomePageRecipeFilterValue",
  SCORE: "beveragesHomePageScoreFilterValue",
  FRESH_WATER_VOLUME: "beveragesHomePageFreshWaterVolumeFilterValue",
  PRE_RINSE_FRESH_WATER: "beveragesHomePagePreRinseFreshWaterFilterValue",
  POST_RINSE_FRESH_WATER: "beveragesHomePagePostRinseFreshWaterFilterValue",
  ALERTS: "beveragesHomePageAlertsFilterValue",
  STATUS: "beveragesHomePageStatusFilterValue",
  DURATION: "beveragesHomePageDurationFilterValue",
  SELECTED_ALERT_TYPES: "beveragesHomePageSelectedAlertTypes",
};

export const clearAllFilterStorage = () => {
  Object.values(FILTER_STORAGE_KEYS).forEach((key) => {
    sessionStorage.removeItem(key);
  });
};

export const initializeFiltersFromURL = (queryParams) => {
  const urlLine = queryParams.get("line") || "";
  const urlObjectName = queryParams.get("objectName") || "";
  const urlRecipe = queryParams.get("recipe") || "";
  const urlShift = queryParams.get("shift") || "All Shifts";
  const urlStatus = queryParams.get("status") || "All Statuses";

  // Handle sort state
  const sortField = queryParams.get("sortField");
  const sortOrder = queryParams.get("sortOrder");
  let sortState = {};
  if (sortField && sortOrder) {
    sortState = { dataField: sortField, order: sortOrder };
  }

  // Handle alerts filter
  const urlAlertsNumber = queryParams.get("alertsNumber");
  const urlAlertsComparator = queryParams.get("alertsComparator");

  // Check if user has visited this dashboard page before in this session
  const hasVisitedBefore = sessionStorage.getItem("beveragesHomePageVisited");

  // Mark that they've visited (for future navigations)
  if (!hasVisitedBefore) {
    sessionStorage.setItem("beveragesHomePageVisited", "true");
  }

  let alertsFilterValue;

  if (urlAlertsNumber && urlAlertsComparator) {
    // User has set a specific alert filter
    alertsFilterValue = {
      number: parseInt(urlAlertsNumber, 10),
      comparator: urlAlertsComparator,
    };
  } else if (hasVisitedBefore) {
    // They've been here before in this session and cleared the filter
    alertsFilterValue = { number: "", comparator: "" };
  } else {
    // First visit in this session = use default
    alertsFilterValue = { number: 1, comparator: Comparator.GE };
  }

  const urlAlertTypes = queryParams.get("alertTypes");
  let selectedAlertTypes = [];
  if (urlAlertTypes) {
    try {
      const alertIds = urlAlertTypes.split(",").filter((id) => id.trim());
      selectedAlertTypes = alertIds.map((id) => ({
        id: id.trim(),
        name:
          ALERT_TYPE_MAPPING[id.trim()] || `Unknown Alert Type ${id.trim()}`,
      }));
    } catch (e) {
      console.warn("Invalid alertTypes in URL:", e);
    }
  }

  return {
    lineFilterValue: urlLine,
    objectNameFilterValue: urlObjectName,
    recipeFilterValue: urlRecipe,
    selectedShift: urlShift,
    selectedStatus: urlStatus,
    selectedAlertTypes,
    sortState,
    // Default values for other filters
    startTimeFilterValue: "",
    shiftFilterValue: "",
    objectGroupFilterValue: "",
    scoreFilterValue: "",
    freshWaterVolumeFilterValue: "",
    preRinseFreshWaterFilterValue: "",
    postRinseFreshWaterFilterValue: "",
    alertsFilterValue: alertsFilterValue,
    statusFilterValue: "",
    durationFilterValue: "",
  };
};

export const updateURLWithFilters = (history, filters) => {
  const newUrl = new URL(window.location.href);

  // Handle line filter
  if (filters.lineFilterValue) {
    newUrl.searchParams.set("line", filters.lineFilterValue);
  } else {
    newUrl.searchParams.delete("line");
  }

  // Handle object name filter
  if (filters.objectNameFilterValue) {
    newUrl.searchParams.set("objectName", filters.objectNameFilterValue);
  } else {
    newUrl.searchParams.delete("objectName");
  }

  // Handle recipe filter
  if (filters.recipeFilterValue) {
    newUrl.searchParams.set("recipe", filters.recipeFilterValue);
  } else {
    newUrl.searchParams.delete("recipe");
  }

  // Handle alert types
  if (filters.selectedAlertTypes && filters.selectedAlertTypes?.length) {
    const alertIds = filters.selectedAlertTypes
      .map((alert) => alert.id)
      .join(",");
    newUrl.searchParams.set("alertTypes", alertIds);
  } else {
    newUrl.searchParams.delete("alertTypes");
  }

  // Handle shift
  if (filters.selectedShift !== "All Shifts") {
    newUrl.searchParams.set("shift", filters.selectedShift);
  } else {
    newUrl.searchParams.delete("shift");
  }

  // Handle status
  if (filters.selectedStatus !== "All Statuses") {
    newUrl.searchParams.set("status", filters.selectedStatus);
  } else {
    newUrl.searchParams.delete("status");
  }

  // Handle alerts filter (number + comparator)
  if (
    filters.alertsFilterValue &&
    filters.alertsFilterValue.number !== undefined
  ) {
    // Always set the params (even if empty) so the component updates
    newUrl.searchParams.set(
      "alertsNumber",
      filters.alertsFilterValue.number.toString()
    );
    newUrl.searchParams.set(
      "alertsComparator",
      filters.alertsFilterValue.comparator.toString()
    );
  } else {
    newUrl.searchParams.delete("alertsNumber");
    newUrl.searchParams.delete("alertsComparator");
  }

  // Handle sort state
  if (filters.sortState && filters.sortState.dataField) {
    newUrl.searchParams.set("sortField", filters.sortState.dataField);
    newUrl.searchParams.set("sortOrder", filters.sortState.order);
  } else {
    newUrl.searchParams.delete("sortField");
    newUrl.searchParams.delete("sortOrder");
  }
  history.replace(newUrl.pathname + newUrl.search);
};

export const extractAlertTypes = (data) => {
  const alertTypesSet = new Set();
  data.forEach((obj) => {
    if (obj.alertsDetailed && obj.alertsDetailed.length > 0) {
      obj.alertsDetailed.forEach((alert) => {
        if (alert.type) {
          alertTypesSet.add(alert.type);
        }
      });
    }
  });
  return Array.from(alertTypesSet).map((type) => ({
    name: ALERT_TYPE_MAPPING[type] || `Unknown Alert Type ${type}`,
    id: type,
  }));
};
